import React, { useState } from 'react';

const SimpleWalletTest = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const addLog = (message) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testEthersImport = () => {
    try {
      addLog('Testing ethers import...');
      const { ethers } = require('ethers');
      addLog(`ethers imported successfully: ${typeof ethers}`);
      addLog(`ethers.BrowserProvider available: ${typeof ethers.BrowserProvider}`);
      addLog(`ethers version: ${ethers.version || 'unknown'}`);
    } catch (error) {
      addLog(`Error importing ethers: ${error.message}`);
    }
  };

  const testWindowEthereum = () => {
    addLog('Testing window.ethereum...');
    if (typeof window.ethereum !== 'undefined') {
      addLog(`window.ethereum found: ${typeof window.ethereum}`);
      addLog(`window.ethereum.isMetaMask: ${window.ethereum.isMetaMask}`);
      addLog(`window.ethereum.isTrust: ${window.ethereum.isTrust || false}`);
      addLog(`window.ethereum.isCoinbaseWallet: ${window.ethereum.isCoinbaseWallet || false}`);
      addLog(`window.ethereum.isBraveWallet: ${window.ethereum.isBraveWallet || false}`);
      addLog(`window.ethereum.isPhantom: ${window.ethereum.isPhantom || false}`);
      addLog(`window.ethereum.selectedAddress: ${window.ethereum.selectedAddress || 'none'}`);
      
      // Detect wallet type
      let walletType = 'Unknown Wallet';
      if (window.ethereum.isMetaMask) walletType = 'MetaMask';
      else if (window.ethereum.isTrust) walletType = 'Trust Wallet';
      else if (window.ethereum.isCoinbaseWallet) walletType = 'Coinbase Wallet';
      else if (window.ethereum.isBraveWallet) walletType = 'Brave Wallet';
      else if (window.ethereum.isPhantom) walletType = 'Phantom';
      
      addLog(`Detected wallet type: ${walletType}`);
    } else {
      addLog('window.ethereum is undefined');
    }
  };

  const testWalletConnection = async () => {
    setIsLoading(true);
    addLog('Testing wallet connection...');
    
    try {
      if (typeof window.ethereum === 'undefined') {
        addLog('No wallet detected');
        return;
      }

      // Try multiple connection methods
      addLog('Trying multiple connection methods...');
      
      // Method 1: Try eth_requestAccounts first
      try {
        addLog('Trying eth_requestAccounts...');
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts.length > 0) {
          addLog(`eth_requestAccounts successful: ${accounts.length} accounts`);
          addLog(`First account: ${accounts[0]}`);
          
          addLog('Creating provider...');
          const { ethers } = require('ethers');
          const provider = new ethers.BrowserProvider(window.ethereum);
          addLog('Provider created successfully');
          
          addLog('Getting signer...');
          const signer = await provider.getSigner();
          addLog('Signer received successfully');
          
          addLog('Getting address...');
          const address = await signer.getAddress();
          addLog(`Address: ${address}`);
          
          addLog('Getting balance...');
          const balance = await provider.getBalance(address);
          addLog(`Balance: ${ethers.formatEther(balance)} ETH`);
          
          addLog('Getting network...');
          const network = await provider.getNetwork();
          addLog(`Network: ${network.name} (Chain ID: ${network.chainId})`);
          
          addLog('Wallet connection test completed successfully!');
          return;
        }
      } catch (err) {
        addLog(`eth_requestAccounts failed: ${err.message}`);
      }

      // Method 2: Try eth_accounts (if already authorized)
      try {
        addLog('Trying eth_accounts...');
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          addLog(`eth_accounts successful: ${accounts.length} accounts`);
          addLog(`First account: ${accounts[0]}`);
          
          addLog('Creating provider...');
          const { ethers } = require('ethers');
          const provider = new ethers.BrowserProvider(window.ethereum);
          addLog('Provider created successfully');
          
          addLog('Getting signer...');
          const signer = await provider.getSigner();
          addLog('Signer received successfully');
          
          addLog('Getting address...');
          const address = await signer.getAddress();
          addLog(`Address: ${address}`);
          
          addLog('Getting balance...');
          const balance = await provider.getBalance(address);
          addLog(`Balance: ${ethers.formatEther(balance)} ETH`);
          
          addLog('Getting network...');
          const network = await provider.getNetwork();
          addLog(`Network: ${network.name} (Chain ID: ${network.chainId})`);
          
          addLog('Wallet connection test completed successfully!');
          return;
        }
      } catch (err) {
        addLog(`eth_accounts failed: ${err.message}`);
      }

      // Method 3: Try legacy enable method (for older wallets)
      if (window.ethereum.enable) {
        try {
          addLog('Trying legacy enable method...');
          const accounts = await window.ethereum.enable();
          if (accounts.length > 0) {
            addLog(`Legacy enable successful: ${accounts.length} accounts`);
            addLog(`First account: ${accounts[0]}`);
            
            addLog('Creating provider...');
            const { ethers } = require('ethers');
            const provider = new ethers.BrowserProvider(window.ethereum);
            addLog('Provider created successfully');
            
            addLog('Getting signer...');
            const signer = await provider.getSigner();
            addLog('Signer received successfully');
            
            addLog('Getting address...');
            const address = await signer.getAddress();
            addLog(`Address: ${address}`);
            
            addLog('Getting balance...');
            const balance = await provider.getBalance(address);
            addLog(`Balance: ${ethers.formatEther(balance)} ETH`);
            
            addLog('Getting network...');
            const network = await provider.getNetwork();
            addLog(`Network: ${network.name} (Chain ID: ${network.chainId})`);
            
            addLog('Wallet connection test completed successfully!');
            return;
          }
        } catch (err) {
          addLog(`Legacy enable failed: ${err.message}`);
        }
      }

      addLog('All connection methods failed');
    } catch (error) {
      addLog(`Error: ${error.message}`);
      console.error('Full error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Simple Wallet Connection Test</h1>
      
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Test Steps</h2>
        <div className="space-y-3">
          <button
            onClick={testEthersImport}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Test Ethers Import
          </button>
          
          <button
            onClick={testWindowEthereum}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors ml-3"
          >
            Test Window Ethereum
          </button>
          
          <button
            onClick={testWalletConnection}
            disabled={isLoading}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors ml-3 disabled:opacity-50"
          >
            {isLoading ? 'Testing...' : 'Test Full Connection'}
          </button>
          
          <button
            onClick={() => {
              addLog('Testing connection methods individually...');
              testWalletConnection();
            }}
            disabled={isLoading}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors ml-3 disabled:opacity-50"
          >
            Test Connection Methods
          </button>
          
          <button
            onClick={clearLogs}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors ml-3"
          >
            Clear Logs
          </button>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold mb-3">Test Logs:</h3>
        <div className="bg-white rounded border p-3 h-64 overflow-y-auto">
          {logs.length === 0 ? (
            <p className="text-gray-500">No logs yet. Click the test buttons above to start testing.</p>
          ) : (
            logs.map((log, index) => (
              <div key={index} className="text-sm font-mono text-gray-700 mb-1">
                {log}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
        <h3 className="font-semibold mb-2">Instructions:</h3>
        <ol className="list-decimal list-inside text-gray-700 space-y-1">
          <li>Click "Test Ethers Import" to verify the library is loaded</li>
          <li>Click "Test Window Ethereum" to check if a wallet is detected</li>
          <li>Click "Test Full Connection" to attempt a complete wallet connection</li>
          <li>Click "Test Connection Methods" to try different connection approaches</li>
          <li>Check the logs below to see what's happening at each step</li>
        </ol>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold mb-2">Troubleshooting MetaMask Issues:</h3>
        <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
          <li><strong>Unexpected Error:</strong> Try refreshing the page or restarting MetaMask</li>
          <li><strong>Connection Pending:</strong> Check if MetaMask is unlocked and has pending requests</li>
          <li><strong>Network Issues:</strong> Try switching networks in MetaMask (Ethereum Mainnet, Goerli, etc.)</li>
          <li><strong>Extension Issues:</strong> Try disabling and re-enabling the MetaMask extension</li>
          <li><strong>Browser Issues:</strong> Try a different browser or incognito mode</li>
        </ul>
      </div>
          </div>
  );
};

export default SimpleWalletTest;
